import { Link } from "@inertiajs/react";
import { useState } from "react";
import type { SjbSalonRow, SjbJobRow, SjbPositionKey, SjbEmploymentKey, SjbVideo } from "./_props";
import { fmt } from "@/lib/_format";
import { salonjobsSalonUrl, salonJobResolvedVideoUrl, salonJobResolvedPosterUrl } from "@/lib/_url";
import { AutoplayVideo } from "./_video";
import { EMPLOYMENT_LABELS } from "./_filter";

export const POSITION_LABELS: Record<SjbPositionKey, string> = {
  stylist: "スタイリスト",
  assistant: "アシスタント",
  stylist_assistant: "スタイリスト/アシスタント",
  eyelist: "アイリスト",
  nailist: "ネイリスト",
  manager: "店長/幹部候補",
  barber: "理容師",
  esthetician: "エステティシャン",
  spanist: "スパニスト",
  unspecified: "未設定",
  other: "その他",
};

export const VIDEO_CATEGORY_LABELS: Record<string, string> = {
  introduction: "サロン紹介",
  interview: "インタビュー",
};

export function positionLabel(position: string): string {
  return POSITION_LABELS[position as SjbPositionKey] || position;
}

export function SalonsGrid({ salons }: { salons: SjbSalonRow[] }) {
  if (salons.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/70 p-12 text-center text-sm text-slate-500">
        条件に合うサロンがありません。フィルタを緩めてみてください。
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {salons.map((s) => (
        <SalonCard key={s.salon_id} salon={s} />
      ))}
    </div>
  );
}

export function SalonCard({ salon }: { salon: SjbSalonRow }) {
  return (
    <Link
      href={salonjobsSalonUrl(salon.salon_id)}
      className="bg-white rounded-xl border border-slate-200/70 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-md hover:border-[#007aff]/40 transition-all overflow-hidden flex flex-col"
    >
      <CardMedia videos={salon.videos} />
      <div className="p-3.5 flex flex-col flex-1 gap-2.5">
        <div>
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="text-[10px] font-semibold text-slate-500 truncate">
              {salon.middle_area || "エリア未設定"}
              {salon.small_area ? ` / ${salon.small_area}` : ""}
            </div>
            <span className="text-[10px] font-bold text-[#007aff] bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
              {fmt(salon.jobs_count)}件の求人
            </span>
          </div>
          <h3 className="text-sm font-bold text-slate-900 leading-tight line-clamp-2">
            {salon.salon_name || "(名称未設定)"}
          </h3>
        </div>

        <div className="flex flex-wrap items-center gap-1">
          {salon.positions.filter((p) => positionLabel(p)).slice(0, 4).map((p) => (
            <span key={p} title={positionLabel(p)} className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded">
              {positionLabel(p)}
            </span>
          ))}
          {salon.employment_categories.filter((e) => EMPLOYMENT_LABELS[e]).slice(0, 3).map((e) => (
            <EmploymentBadge key={e} employment={e} />
          ))}
        </div>

        <div className="border-t border-slate-100 pt-2 mt-auto">
          <div className="text-[10px] font-semibold text-slate-500">給与レンジ（全求人合計）</div>
          <div className="text-sm font-bold text-slate-900 tabular-nums">
            {formatSalaryRange(salon.salary_min_overall, salon.salary_max_overall)}
          </div>
        </div>

        <ConditionTags salon={salon} />
        <JobsSummaryList jobs={salon.jobs} />
      </div>
    </Link>
  );
}

export function JobsSummaryList({ jobs }: { jobs: SjbJobRow[] }) {
  const visible = jobs.slice(0, 3);
  if (visible.length === 0) return null;
  return (
    <div className="border-t border-slate-100 pt-2 space-y-1">
      {visible.map((j) => {
        const title = formatJobTitle(j);
        const salary = formatSalary(j.salary_min_amount, j.salary_max_amount, j.salary_summary);
        return (
          <div key={j.job_id} className="flex items-center justify-between text-[11px] gap-2">
            <span className="text-slate-700 truncate flex-1">{title}</span>
            {salary && (
              <span className="text-slate-900 font-semibold tabular-nums shrink-0">
                {salary}
              </span>
            )}
          </div>
        );
      })}
      {jobs.length > visible.length && (
        <div className="text-[10px] text-slate-400 text-right">他 {jobs.length - visible.length} 件</div>
      )}
    </div>
  );
}

function formatJobTitle(job: SjbJobRow): string {
  const parts = [
    job.position_normalized ? positionLabel(job.position_normalized) : "",
    job.employment_category_id ? EMPLOYMENT_LABELS[job.employment_category_id] : "",
  ].filter(Boolean);
  return parts.join(" ・ ");
}

export function CardMedia({ videos }: { videos: SjbVideo[] }) {
  const [idx, setIdx] = useState(0);
  if (videos.length === 0) {
    return (
      <div className="aspect-[16/9] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <span className="text-[10px] text-slate-400 font-semibold">画像なし</span>
      </div>
    );
  }
  const current = videos[idx];
  const advance = () => videos.length > 1 && setIdx((i) => (i + 1) % videos.length);
  const label = current.category_id ? VIDEO_CATEGORY_LABELS[current.category_id] || "動画" : "動画";
  return (
    <div className="relative aspect-[16/9] bg-slate-900 overflow-hidden">
      <AutoplayVideo
        key={current.video_id}
        src={salonJobResolvedVideoUrl(current)}
        poster={salonJobResolvedPosterUrl(current)}
        onEnded={advance}
        loop={false}
        lazy
      />
      <div className="absolute top-2 left-2 flex flex-wrap gap-1 pointer-events-none">
        <span className="text-[9px] font-bold bg-white/90 text-slate-900 px-1.5 py-0.5 rounded shadow-sm">
          {label}
        </span>
        {videos.length > 1 && (
          <span className="text-[9px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded">
            {idx + 1}/{videos.length}
          </span>
        )}
      </div>
    </div>
  );
}

export function EmploymentBadge({ employment }: { employment: SjbEmploymentKey }) {
  const cls = {
    full_time: "bg-blue-100 text-blue-700",
    freelance: "bg-purple-100 text-purple-700",
    part_time: "bg-amber-100 text-amber-700",
    contract: "bg-slate-100 text-slate-700",
  }[employment];
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${cls}`}>
      {EMPLOYMENT_LABELS[employment]}
    </span>
  );
}

export function formatSalary(min: number | null, max: number | null, summary: string | null): string {
  if (!min && !max) return summary || "";
  if (min && max && min !== max) return `${fmtAmt(min)} 〜 ${fmtAmt(max)}`;
  if (min && max && min === max) return fmtAmt(min);
  if (min) return `${fmtAmt(min)} 〜`;
  if (max) return `〜 ${fmtAmt(max)}`;
  return summary || "";
}

export function formatSalaryRange(min: number | null, max: number | null): string {
  if (!min && !max) return "給与未設定";
  if (min && max && min !== max) return `${fmtAmt(min)} 〜 ${fmtAmt(max)}`;
  if (min && max && min === max) return fmtAmt(min);
  if (min) return `${fmtAmt(min)} 〜`;
  if (max) return `〜 ${fmtAmt(max)}`;
  return "給与未設定";
}

export function fmtAmt(n: number): string {
  if (n >= 10000) {
    const man = Math.floor(n / 10000);
    const rem = n % 10000;
    return rem === 0 ? `${man}万` : `${man}.${Math.floor(rem / 1000)}万`;
  }
  return `${fmt(n)}円`;
}

export function ConditionTags({ salon }: { salon: SjbSalonRow }) {
  const tags: string[] = [];
  if (salon.has_social_insurance) tags.push("社保");
  if (salon.has_training_for_beginners) tags.push("未経験OK");
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t) => (
        <span key={t} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
          {t}
        </span>
      ))}
    </div>
  );
}
